export type SemanticShapeElementId =
  | 'straight'
  | 'turn_left'
  | 'turn_right'
  | 'hill'
  | 'drop'
  | 'loop'
  | 'loop_left'
  | 'loop_right'
  | 'roll_left'
  | 'roll_right'
  | 'corkscrew_left'
  | 'corkscrew_right';

export interface SemanticControlPoint {
  /** Normalized progress through the generated element. */
  readonly progress: number;
  /** Distance in the entry tangent's direction. */
  readonly forwardM: number;
  /** Horizontal distance to the entry tangent's right. */
  readonly sideM: number;
  /** Height above the element's interpolated entry-to-exit baseline. */
  readonly heightM: number;
  /** Unwrapped bank added to the bank at the element entrance. */
  readonly bankDeltaDegrees: number;
}

export interface SemanticElementOptions {
  readonly chordLengthM: number;
  readonly includeExit: boolean;
}

const DEFAULT_APPEND_LENGTHS: Readonly<Record<SemanticShapeElementId, number>> = {
  straight: 14,
  turn_left: 12,
  turn_right: 12,
  hill: 24,
  drop: 20,
  loop: 14,
  loop_left: 14,
  loop_right: 14,
  roll_left: 30,
  roll_right: 30,
  corkscrew_left: 32,
  corkscrew_right: 32,
};

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function smootherStep(progress: number): number {
  return progress * progress * progress * (progress * (progress * 6 - 15) + 10);
}

/**
 * A symmetric pulse with zero first and second derivative at both ends.
 * It gives hills and inversions eased transitions instead of circular jolts.
 */
function fairingPulse(progress: number): number {
  return 256 * progress ** 4 * (1 - progress) ** 4;
}

function sampled(
  segments: number,
  includeExit: boolean,
  sample: (progress: number) => Omit<SemanticControlPoint, 'progress'>,
): SemanticControlPoint[] {
  const last = includeExit ? segments : segments - 1;
  return Array.from({ length: last }, (_, index) => {
    const progress = (index + 1) / segments;
    return { progress, ...sample(progress) };
  });
}

function straight(chordLengthM: number, includeExit: boolean): SemanticControlPoint[] {
  return sampled(3, includeExit, (progress) => ({
    forwardM: chordLengthM * progress,
    sideM: 0,
    heightM: 0,
    bankDeltaDegrees: 0,
  }));
}

function turn(direction: -1 | 1, radiusM: number, includeExit: boolean): SemanticControlPoint[] {
  return sampled(6, includeExit, (progress) => {
    const angle = progress * Math.PI * 0.5;
    return {
      forwardM: radiusM * Math.sin(angle),
      sideM: direction * radiusM * (1 - Math.cos(angle)),
      heightM: 0,
      bankDeltaDegrees: direction * 28 * Math.sin(Math.PI * progress) ** 2,
    };
  });
}

function replacementTurn(
  direction: -1 | 1,
  chordLengthM: number,
  includeExit: boolean,
): SemanticControlPoint[] {
  const offsetM = clamp(chordLengthM * 0.4, 4, 12);
  return sampled(6, includeExit, (progress) => ({
    forwardM: chordLengthM * progress,
    sideM: direction * offsetM * Math.sin(Math.PI * progress),
    heightM: 0,
    bankDeltaDegrees: direction * 24 * Math.sin(Math.PI * progress) ** 2,
  }));
}

function hill(chordLengthM: number, includeExit: boolean): SemanticControlPoint[] {
  const heightM = clamp(chordLengthM * 0.5, 10, 16);
  return sampled(8, includeExit, (progress) => ({
    forwardM: chordLengthM * progress,
    sideM: 0,
    heightM: heightM * fairingPulse(progress),
    bankDeltaDegrees: 0,
  }));
}

function drop(
  chordLengthM: number,
  includeExit: boolean,
  returnToBaseline: boolean,
): SemanticControlPoint[] {
  const depthM = clamp(chordLengthM * 0.64, 12, 18);
  return sampled(8, includeExit, (progress) => ({
    forwardM: chordLengthM * progress,
    sideM: 0,
    heightM: returnToBaseline ? -depthM * fairingPulse(progress) : -depthM * smootherStep(progress),
    bankDeltaDegrees: 0,
  }));
}

/**
 * Generates a clothoid-inspired handed teardrop loop.
 *
 * The fairing pulse makes entry/exit curvature approach zero. A smaller
 * effective radius around the crown creates the teardrop silhouette expected
 * from a modern steel loop. The periodic component reverses the centerline's
 * forward direction through the crown. A handed lateral bulge plus separate
 * ascending and descending lanes prevents the rails from occupying the same
 * horizontal centerline. The exit remains one track-width to the selected side
 * instead of converging back onto the entry centerline. Bank remains unchanged
 * because the transported track frame performs the inversion.
 */
export function generateVerticalLoopProfile(
  chordLengthM = DEFAULT_APPEND_LENGTHS.loop,
  includeExit = true,
  direction: -1 | 1 = 1,
): SemanticControlPoint[] {
  const chord = clamp(chordLengthM, 8, 28);
  const heightM = clamp(chord * 1.8, 24, 28);
  const bottomAmplitudeM = heightM * 0.55;
  const crownAmplitudeM = heightM * 0.42;
  const exitOffsetM = 5.5;
  return sampled(20, includeExit, (progress) => {
    const phase = progress * Math.PI * 2;
    const crownWeight = Math.sin(Math.PI * progress) ** 2;
    const effectiveAmplitudeM =
      bottomAmplitudeM + (crownAmplitudeM - bottomAmplitudeM) * crownWeight;
    const easedLoopComponent = Math.sin(phase);
    const lateralEnvelope = Math.sin(Math.PI * progress) ** 2;
    const laneShiftM = direction * exitOffsetM * smootherStep(progress);
    const handedBulgeM = direction * 3 * lateralEnvelope;
    const branchSeparationM = direction * 2 * Math.sin(phase) * lateralEnvelope;
    return {
      forwardM: chord * progress + effectiveAmplitudeM * easedLoopComponent,
      sideM: laneShiftM + handedBulgeM + branchSeparationM,
      heightM: heightM * (1 - Math.cos(phase)) * 0.5,
      bankDeltaDegrees: 0,
    };
  });
}

function replacementLoop(
  direction: -1 | 1,
  chordLengthM: number,
  includeExit: boolean,
): SemanticControlPoint[] {
  const loopChordM = clamp(chordLengthM * 0.7, 10, 18);
  const approachM = Math.max(0, (chordLengthM - loopChordM) * 0.5);
  const loopExitM = approachM + loopChordM;
  const points: SemanticControlPoint[] = [];
  if (approachM > 1) {
    points.push({
      progress: (approachM * 0.5) / chordLengthM,
      forwardM: approachM * 0.5,
      sideM: 0,
      heightM: 0,
      bankDeltaDegrees: 0,
    });
  }
  const loopPoints = generateVerticalLoopProfile(loopChordM, true, direction);
  const loopExitSideM = loopPoints.at(-1)?.sideM ?? 0;
  points.push(
    ...loopPoints.map((point) => ({
      ...point,
      progress: (approachM + point.progress * loopChordM) / chordLengthM,
      forwardM: approachM + point.forwardM,
    })),
  );
  if (chordLengthM - loopExitM > 1) {
    points.push({
      progress: (loopExitM + (chordLengthM - loopExitM) * 0.5) / chordLengthM,
      forwardM: loopExitM + (chordLengthM - loopExitM) * 0.5,
      sideM: loopExitSideM,
      heightM: 0,
      bankDeltaDegrees: 0,
    });
  }
  if (includeExit) {
    points.push({
      progress: 1,
      forwardM: chordLengthM,
      sideM: loopExitSideM,
      heightM: 0,
      bankDeltaDegrees: 0,
    });
  }
  return points;
}

function heartlineRoll(
  direction: -1 | 1,
  chordLengthM: number,
  includeExit: boolean,
): SemanticControlPoint[] {
  const points = sampled(12, includeExit, (progress) => ({
    forwardM: chordLengthM * progress,
    sideM: direction * 1.6 * Math.sin(progress * Math.PI * 2) * Math.sin(Math.PI * progress) ** 2,
    heightM: 2.2 * fairingPulse(progress),
    bankDeltaDegrees: direction * 360 * smootherStep(progress),
  }));
  if (!includeExit && points.length)
    points[points.length - 1] = {
      ...points[points.length - 1]!,
      bankDeltaDegrees: direction * 360,
    };
  return points;
}

function corkscrew(
  direction: -1 | 1,
  chordLengthM: number,
  includeExit: boolean,
): SemanticControlPoint[] {
  const radiusM = clamp(chordLengthM * 0.17, 4.5, 6);
  const points = sampled(14, includeExit, (progress) => {
    const phase = progress * Math.PI * 2;
    const envelope = Math.sin(Math.PI * progress) ** 2;
    return {
      forwardM: chordLengthM * progress,
      sideM: direction * radiusM * envelope * Math.sin(phase),
      heightM: radiusM * envelope * (1 - Math.cos(phase)),
      bankDeltaDegrees: direction * 360 * smootherStep(progress),
    };
  });
  if (!includeExit && points.length)
    points[points.length - 1] = {
      ...points[points.length - 1]!,
      bankDeltaDegrees: direction * 360,
    };
  return points;
}

export function generateSemanticElementPoints(
  id: SemanticShapeElementId,
  options?: Partial<SemanticElementOptions> & { readonly replacement?: boolean },
): SemanticControlPoint[] {
  const chordLengthM = Math.max(4, options?.chordLengthM ?? DEFAULT_APPEND_LENGTHS[id]);
  const includeExit = options?.includeExit ?? true;
  switch (id) {
    case 'straight':
      return straight(chordLengthM, includeExit);
    case 'turn_left':
      return options?.replacement
        ? replacementTurn(-1, chordLengthM, includeExit)
        : turn(-1, chordLengthM, includeExit);
    case 'turn_right':
      return options?.replacement
        ? replacementTurn(1, chordLengthM, includeExit)
        : turn(1, chordLengthM, includeExit);
    case 'hill':
      return hill(chordLengthM, includeExit);
    case 'drop':
      return drop(chordLengthM, includeExit, options?.replacement ?? false);
    case 'loop':
    case 'loop_right':
      return options?.replacement
        ? replacementLoop(1, chordLengthM, includeExit)
        : generateVerticalLoopProfile(chordLengthM, includeExit, 1);
    case 'loop_left':
      return options?.replacement
        ? replacementLoop(-1, chordLengthM, includeExit)
        : generateVerticalLoopProfile(chordLengthM, includeExit, -1);
    case 'roll_left':
      return heartlineRoll(-1, chordLengthM, includeExit);
    case 'roll_right':
      return heartlineRoll(1, chordLengthM, includeExit);
    case 'corkscrew_left':
      return corkscrew(-1, chordLengthM, includeExit);
    case 'corkscrew_right':
      return corkscrew(1, chordLengthM, includeExit);
  }
}

export function isSemanticInversion(id: string): boolean {
  return id.startsWith('loop') || id.startsWith('roll_') || id.startsWith('corkscrew_');
}
